import axios from 'axios';
import { routeToServerlessAPI } from './errorHandler';

let serverConfig = null;

export const setServerConfig = (config) => {
    serverConfig = config;
};

export const getServerConfig = () => serverConfig;

const getApiBaseUrl = (alb) => {
    return `https://api${alb > 1 ? alb : ''}.tradealm.com`;
};

// Returns the ECS base URL: server-specific if migrated, old proxy if not.
const getEcsBaseUrl = () => {
    if (serverConfig) {
        return getApiBaseUrl(serverConfig.alb);
    }
    return process.env.REACT_APP_API_URL;
};

/**
 * Make an API request with automatic ECS/Lambda routing and failover.
 *
 * During ECS hours (6am-2pm MT weekdays), routes to the user's assigned
 * ECS server via getApiBaseUrl, or to the old proxy (REACT_APP_API_URL)
 * for users not yet migrated. Outside those hours or on error (network/503),
 * falls back to the Lambda API.
 *
 * @param {string} method - 'get', 'post', or 'put'
 * @param {string} ecsPath - The ECS server path (e.g., '/algo/demo/data/start')
 * @param {string} lambdaPath - The Lambda path (e.g., '/api/v2/data/start')
 * @param {object} options - { headers, params, body }
 * @returns {Promise} Axios response
 */
export const apiRequest = async (method, ecsPath, lambdaPath, { headers, params, body } = {}, useServerlessApi = false) => {
    const useServerless = routeToServerlessAPI();

    if (!useServerlessApi && !useServerless) {
        const baseUrl = getEcsBaseUrl();
        const ecsParams = withServerName(params);
        try {
            return await axiosCall(method, `${baseUrl}${ecsPath}`, { headers, params: ecsParams, body });
        } catch (error) {
            if (routeToServerlessAPI(error)) {
                // ECS failed with network error or 503, fall through to serverless
            } else {
                throw error;
            }
        }
    }

    // Serverless (Lambda) fallback
    const serverlessUrl = process.env.REACT_APP_SERVERLESS_API_URL;
    return await axiosCall(method, `${serverlessUrl}${lambdaPath}`, { headers, params, body });
};

/**
 * Make an ECS-only API request (no Lambda fallback).
 * Returns null if ECS is not available (outside hours).
 */
export const ecsRequest = async (method, ecsPath, { headers, params, body } = {}) => {
    if (routeToServerlessAPI()) {
        return null;
    }
    const baseUrl = getEcsBaseUrl();
    return await axiosCall(method, `${baseUrl}${ecsPath}`, { headers, params: withServerName(params), body });
};

// For migrated users, attach serverName so the ALB can route to the correct target group.
// Unmigrated users only send uid (handled by the old proxy).
const withServerName = (params) => {
    if (serverConfig && serverConfig.serverName) {
        return { ...params, serverName: serverConfig.serverName };
    }
    return params;
};

const axiosCall = async (method, url, { headers, params, body } = {}) => {
    const config = { headers, params };
    switch (method) {
        case 'get':
            return await axios.get(url, config);
        case 'post':
            return await axios.post(url, body, config);
        case 'put':
            return await axios.put(url, body, config);
        case 'delete':
            return await axios.delete(url, config);
        default:
            throw new Error(`Unsupported HTTP method: ${method}`);
    }
};
